import { useCallback, useEffect, useState } from "react";
import config from "../config";
import { cachedPost } from "../apiCache";
import HomeHero from "./HomeHero";
import HomeQuickLinks from "./HomeQuickLinks";
import HomeProgress from "./HomeProgress";
// Continue learning is commented out on request — the row it lived in is gone
// and its place on the page is now the Recent community posts block. Restore
// this import together with the block further down.
// import HomeContinue from "./HomeContinue";
import { HomeCalendar, HomeUpcoming, HomeTopContributors, HomeChampions } from "./HomeRail";
import { alpha, initialsOf, cardCls, HomeSectionTitle, HomeLink, HomeEmpty, BrandButton, Skeleton } from "./homeUi";

/**
 * The member Home dashboard — /membership/:token/home.
 *
 * Opt-in per membership (Settings → Membership Menus → Home); when on it is
 * where members land after login. One request (/membership-home) feeds every
 * block except Continue learning, which reuses the course catalogue's own
 * cached fetch.
 *
 * Every block respects the creator's menus: a block whose section is switched
 * off isn't a dead end on this page, it just isn't here. Rendered inside
 * MembershipShell, so the header, sidebar and template chrome are the shell's;
 * the shell also hands in `renderPost`, so a post here is the feed's real
 * PostCard — like, comment and poll all work in place.
 */

function HomeSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading your home">
      <div className={`${cardCls} space-y-5 p-6 sm:p-7`}>
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-8 w-72 max-w-full" />
        <div className="flex gap-2">
          <Skeleton className="h-7 w-24 rounded-full" />
          <Skeleton className="h-7 w-20 rounded-full" />
          <Skeleton className="h-7 w-28 rounded-full" />
        </div>
        <Skeleton className="h-2 w-full" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[70px]" />)}
        </div>
      </div>
      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[76px] rounded-2xl" />)}
      </div>
      <div className="grid gap-6 lg:grid-cols-12">
        <Skeleton className="h-80 rounded-2xl lg:col-span-8" />
        <Skeleton className="h-80 rounded-2xl lg:col-span-4" />
      </div>
    </div>
  );
}

export default function HomeView({ token, member, primary, community, onNavigate, onOpenProfile, onOpenGroup, onOpenCourse, renderPost }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [posts, setPosts] = useState([]);
  // Continue learning reported when it had nothing to show, so its row could give
  // the whole width to the checklist instead of leaving a hole. Restore with the
  // block below.
  // const [noCourse, setNoCourse] = useState(false);

  const load = useCallback(
    async (force = false) => {
      if (!member?.member_token) return;
      setError("");
      try {
        const res = await cachedPost(
          `${config.COMMUNITY_API_URL}/membership-home`,
          { community_token: token, member_token: member.member_token },
          { ttl: 20000, force }
        );
        if (res.data?.error === 0) {
          setData(res.data);
          setPosts(res.data.recent_posts || []);
        } else {
          setError(res.data?.msg || "We couldn’t load your home right now.");
        }
      } catch {
        setError("We couldn’t load your home right now.");
      }
    },
    [token, member?.member_token]
  );

  useEffect(() => {
    load();
  }, [load]);

  // A block shows only when its menu is on. The endpoint's `menus` is the
  // freshest copy; the community payload's is the fallback while it loads.
  const menus = data?.menus || community?.menus || {};
  const menuOn = (key) => Number(menus[key]) !== 1;

  if (!member) {
    return (
      <div className={`${cardCls} p-10`}>
        <HomeEmpty primary={primary} icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" /></svg>} title="Sign in to see your home" text="Your progress, courses and community activity live here." />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className={`${cardCls} p-10`}>
        <HomeEmpty
          primary={primary}
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>}
          title="Your home didn’t load"
          text={error}
          action={<BrandButton primary={primary} onClick={() => load(true)}>Try again</BrandButton>}
        />
      </div>
    );
  }
  if (!data) return <HomeSkeleton />;

  const gamification = data.gamification || {};
  // const showCourses = menuOn("course");
  const showPosts = menuOn("community");
  const showEvents = menuOn("event");
  const upcoming = data.events?.upcoming || [];
  const tz = data.events?.timezone || "";
  const onAction = (action) => (action === "profile" ? onOpenProfile() : onNavigate(action));
  // const courseCol = showCourses && !noCourse;

  const rail = [
    /* Gamification rides in the rail, at the calendar's width, now that the
       Continue learning row it used to share is gone. It stays first: it is the
       member's own standing, and the events below it are the community's.
       Only when the creator has actually set points up — a membership without
       them gets no card at all here, rather than one explaining an absence. */
    gamification.enabled ? (
      <HomeProgress key="gp" me={data.me} gamification={gamification} checklist={data.checklist} primary={primary} menuOn={menuOn} onAction={onAction} compact />
    ) : null,
    showEvents ? <HomeCalendar key="cal" events={upcoming} tz={tz} primary={primary} onOpenEvents={() => onNavigate("events")} /> : null,
    showEvents ? <HomeUpcoming key="up" events={upcoming} tz={tz} primary={primary} onOpenEvents={() => onNavigate("events")} /> : null,
    gamification.enabled ? (
      <HomeTopContributors key="top" top={data.top} me={data.me} primary={primary} onOpenBoard={menuOn("leaderboard") ? () => onNavigate("leaderboard") : null} />
    ) : null,
    showPosts ? <HomeChampions key="champ" champions={data.champions} primary={primary} /> : null,
  ].filter(Boolean);

  return (
    <div className="space-y-7">
      <HomeHero me={data.me} stats={data.stats} gamification={gamification} menuOn={menuOn} primary={primary} />

      <HomeQuickLinks quick={data.quick_links} menuOn={menuOn} primary={primary} onNavigate={onNavigate} />

      {/* Continue learning + gamification row — commented out on request. Kept,
          not deleted: gamification moved into the rail below (same width as the
          Events calendar), and Recent community posts now takes this spot. To
          restore, uncomment this block, the HomeContinue import, the `noCourse`
          state and the two derived flags above, and drop the <HomeProgress> entry
          out of `rail`.

      <div className={`grid items-stretch gap-6 ${courseCol ? "lg:grid-cols-12" : ""}`}>
        {showCourses ? (
          <div className={noCourse ? "hidden" : "lg:col-span-5"}>
            <HomeContinue member={member} primary={primary} onOpenCourse={onOpenCourse} onAll={() => onNavigate("courses")} onEmpty={setNoCourse} />
          </div>
        ) : null}
        <div className={courseCol ? "lg:col-span-7" : ""}>
          <HomeProgress me={data.me} gamification={gamification} checklist={data.checklist} primary={primary} menuOn={menuOn} onAction={onAction} />
        </div>
      </div>
      */}

      {showPosts || rail.length ? (
        <div className={`grid items-start gap-6 ${showPosts && rail.length ? "lg:grid-cols-12" : ""}`}>
          {showPosts ? (
            <section className={rail.length ? "min-w-0 lg:col-span-8" : "min-w-0"} aria-label="Recent community posts">
              <HomeSectionTitle
                accent
                primary={primary}
                title="Recent community posts"
                subtitle="See what members are asking, sharing and building."
                action={posts.length ? <HomeLink primary={primary} onClick={() => onNavigate("groups")}>View all</HomeLink> : null}
              />
              {posts.length ? (
                <div className="space-y-5">
                  {posts.map((p) => (
                    <div key={p.post_id} className="min-w-0">
                      {/* Which group this came from — posts here are drawn from
                          every group the member can read — and the way in. */}
                      {p.group_name ? (
                        <button
                          type="button"
                          onClick={() => onOpenGroup(p.group_token)}
                          className="group mb-2 flex w-full items-center gap-2.5 rounded-xl px-1 py-1 text-left transition"
                          title={`Open ${p.group_name}`}
                        >
                          <span
                            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[11px] font-extrabold"
                            style={{ backgroundColor: alpha(primary, 0.12), color: primary }}
                          >
                            {initialsOf(p.group_name)}
                          </span>
                          <span className="min-w-0 truncate text-[12.5px] text-gray-500">
                            Posted in <span className="font-bold text-gray-800 group-hover:underline">{p.group_name}</span>
                          </span>
                          <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold opacity-80 transition group-hover:opacity-100" style={{ color: primary }}>
                            Open community
                            <svg className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg>
                          </span>
                        </button>
                      ) : null}
                      {renderPost(p, { onDeleted: () => setPosts((ps) => ps.filter((x) => x.post_id !== p.post_id)) })}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`${cardCls} p-5`}>
                  <HomeEmpty
                    primary={primary}
                    icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" /></svg>}
                    title="No posts yet"
                    text="Be the first to start a conversation — introduce yourself or share a win."
                    action={<BrandButton primary={primary} onClick={() => onNavigate("groups")}>Go to community</BrandButton>}
                  />
                </div>
              )}
            </section>
          ) : null}

          {rail.length ? (
            <aside className={showPosts ? "space-y-5 lg:col-span-4" : "grid gap-5 sm:grid-cols-2 xl:grid-cols-3"} aria-label="Community">
              {rail}
            </aside>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
